import { Platform } from 'react-native';
import appJson from '../../app.json';
import { API_BASE } from '../config';
import { scrubTelemetryText } from './telemetryPath';

/**
 * Отправка поломок клиента на свой бэкенд (OBS-2), откуда они уезжают в GlitchTip.
 *
 * До этого модуля клиентская сторона была невидима: Crashlytics ловил только нативные падения,
 * его консоль — отдельное место, куда никто не смотрит, а веб не отправлял никуда ничего. Отказ
 * входа, сорвавшийся звонок и неотправленное сообщение не оставляли следа вообще нигде — ни на
 * устройстве, ни на сервере.
 *
 * Приёмник — `POST /api/client-errors`, ручка на нашем же API. Не DSN GlitchTip напрямую, и это
 * решение, а не упрощение: у GlitchTip нет публичного адреса (см. `deploy/RUNBOOK.md`), и
 * открывать ему дверь наружу ради этого значило бы завести ещё один вход в сервер. Плюс к тому в
 * собранном приложении не оказывается никакого секрета, а серверная и клиентская стороны одного
 * случая попадают в один журнал — их связывает `requestId`.
 *
 * Четыре правила, унаследованные от `analytics.ts`, потому что они там уже оплачены опытом:
 *
 * 1. **Отчёт не ломает приложение.** Ни одна функция отсюда не бросает наружу и ничего не ждёт.
 *    Телеметрия, из-за которой падает мессенджер, хуже отсутствующей телеметрии.
 * 2. **Отчёт не трогает сессию.** Отправка идёт голым `fetch`, а не через `apiFetch`: тот при 401
 *    вызывает `notifyAuthInvalid()` и выкидывает человека из аккаунта. Сообщение о поломке не
 *    имеет права никого разлогинивать.
 * 3. **Отчёт уходит и без токена.** Ручка на сервере открыта — иначе она не увидела бы ровно тот
 *    случай, ради которого написана: приложение сломалось на экране входа.
 * 4. **В отчёт не попадает переписка.** Сюда передаются коды, идентификаторы и состояния — ни
 *    текста сообщений, ни имён, ни заголовков. Единственное свободное поле — `message`, и в него
 *    кладётся текст ошибки, а не содержимое экрана.
 */

/** Что именно сломалось. Совпадает с полем `scope` на сервере. */
export type ErrorScope = 'auth' | 'call' | 'message' | 'media' | 'realtime' | 'push' | 'app';

/**
 * `fatal` — приложение упало, `error` — экран сломался, но приложение живо, `warning` —
 * обработанная неудача, о которой стоит знать. Первые два становятся происшествием в GlitchTip и
 * поднимают телеграм-бота; `warning` только пишется в журнал.
 */
export type ErrorLevel = 'fatal' | 'error' | 'warning';

export type ReportOptions = {
  level?: ErrorLevel;
  /** Коды, идентификаторы, состояния. Значения режутся по длине на сервере. */
  context?: Record<string, string | number | boolean | null | undefined>;
  /** `X-Request-Id` неудачного запроса, если он известен точно. */
  requestId?: string;
};

/** Столько последних шагов уезжает вместе с отчётом. Больше — и строка в журнале нечитаема. */
const MAX_BREADCRUMBS = 12;

/** Повтор той же поломки в этом окне не отправляется: цикл перерисовки иначе завалит приёмник. */
const DEDUPE_WINDOW_MS = 10_000;

/** Столько отчётов за минуту максимум. Свой потолок, до серверного лимита по адресу. */
const MAX_REPORTS_PER_MINUTE = 10;

const REQUEST_TIMEOUT_MS = 8_000;

type Breadcrumb = { at: number; scope: ErrorScope; message: string };

let breadcrumbs: Breadcrumb[] = [];
let currentUserId: string | null = null;
let lastRequestId: string | null = null;
let lastSent = new Map<string, number>();
let sentTimestamps: number[] = [];

/**
 * Токен читается лениво и через переданный сюда getter, а не импортом из `api/client`: тот
 * импортирует этот модуль сам, и прямая ссылка замкнула бы цикл. Устанавливается один раз при
 * старте (`App.tsx`).
 */
let accessTokenReader: (() => Promise<string | null>) | null = null;

export function configureErrorReporter(readAccessToken: () => Promise<string | null>): void {
  accessTokenReader = readAccessToken;
}

/** Кто сейчас в приложении. Ставится после входа и снимается при выходе. */
export function setReportUser(userId: string | null): void {
  currentUserId = userId;
}

/**
 * Последний идентификатор запроса, который вернул сервер. Держится один, а не список: отчёт
 * почти всегда рождается сразу после неудачного запроса, и нужен именно он.
 */
export function noteRequestId(requestId: string | null | undefined): void {
  if (requestId) {
    lastRequestId = requestId;
  }
}

/**
 * Шаг, который сам по себе не поломка, но объясняет следующую: «ушёл в фон», «сокет переподключён»,
 * «нажал позвонить». Никуда не отправляется до тех пор, пока что-нибудь не сломается.
 */
export function breadcrumb(scope: ErrorScope, message: string): void {
  try {
    // Пути внутри шага — только шаблоном (PRIV-3), кто бы его сюда ни положил.
    breadcrumbs.push({ at: Date.now(), scope, message: scrubTelemetryText(String(message)).slice(0, 120) });
    if (breadcrumbs.length > MAX_BREADCRUMBS) {
      breadcrumbs = breadcrumbs.slice(-MAX_BREADCRUMBS);
    }
  } catch {
    /* см. правило 1 в шапке модуля */
  }
}

export function appVersion(): string {
  try {
    const expo = appJson.expo as {
      version?: string;
      ios?: { buildNumber?: string };
      android?: { versionCode?: number };
    };
    const version = expo.version ?? '0.0.0';
    // Версия в app.json не менялась с 1.0.0, а номер сборки меняется каждый раз — без него
    // нельзя отличить отчёты двух разных бинарников, то есть нельзя увидеть регрессию по сборкам.
    const desktop = desktopShellVersion();
    const build =
      Platform.OS === 'ios'
        ? expo.ios?.buildNumber
        : Platform.OS === 'android'
          ? expo.android?.versionCode
          : desktop
            ? `desktop.${desktop}`
            : undefined;
    return build === undefined ? version : `${version}+${build}`;
  } catch {
    return '0.0.0';
  }
}

/**
 * Версия десктопной оболочки (`desktop/package.json`), которую preload кладёт в
 * `window.vrmDesktop`. Веб внутри десктопа вшит в установщик и обновляется только вместе с
 * ним, поэтому без этой приписки отчёт «[web] версия=1.0.0» не отличить ни от сайта, ни от
 * десктопа, который не обновили (так было с GlitchTip #20 и #21).
 */
function desktopShellVersion(): string | null {
  if (typeof window === 'undefined') return null;
  const shell = (window as unknown as { vrmDesktop?: { appVersion?: unknown } }).vrmDesktop;
  const v = shell?.appVersion;
  // Не длиннее 16: `appVersion` аналитики на сервере ограничен 32 символами
  // (AnalyticsIngestRequest), и «1.0.0+desktop.» из них уже занимает 14.
  return typeof v === 'string' && /^[0-9A-Za-z.-]{1,16}$/.test(v) ? v : null;
}

export function platformName(): 'ios' | 'android' | 'web' {
  return Platform.OS === 'ios' ? 'ios' : Platform.OS === 'android' ? 'android' : 'web';
}

function osVersion(): string {
  try {
    if (Platform.OS === 'web') {
      return String(globalThis.navigator?.userAgent ?? 'web').slice(0, 64);
    }
    return `${Platform.OS} ${String(Platform.Version)}`;
  } catch {
    return '-';
  }
}

function messageOf(error: unknown): string {
  if (error instanceof Error) {
    return error.message ? `${error.name}: ${error.message}` : error.name;
  }
  if (typeof error === 'string') {
    return error;
  }
  try {
    return JSON.stringify(error).slice(0, 300);
  } catch {
    return String(error);
  }
}

function stackOf(error: unknown): string | undefined {
  if (!(error instanceof Error) || typeof error.stack !== 'string') {
    return undefined;
  }
  // Первая строка стека повторяет текст ошибки — с тем же адресом, что вычищен из `message`
  // (PRIV-3). Кадры ниже — файлы бандла, их не трогаем: без них стек бесполезен.
  return error.message ? error.stack.split(error.message).join(scrubTelemetryText(error.message)) : error.stack;
}

/**
 * Два потолка сразу. Дедупликация по тексту гасит цикл перерисовки, который шлёт одно и то же
 * тысячу раз в секунду; общий потолок в минуту — всё остальное, до чего дедупликация не достаёт.
 */
function allowedToSend(key: string): boolean {
  const now = Date.now();
  const previous = lastSent.get(key);
  if (previous !== undefined && now - previous < DEDUPE_WINDOW_MS) {
    return false;
  }
  sentTimestamps = sentTimestamps.filter((t) => now - t < 60_000);
  if (sentTimestamps.length >= MAX_REPORTS_PER_MINUTE) {
    return false;
  }
  // Карта не растёт бесконечно: держим только то, что попало в окно дедупликации.
  if (lastSent.size > 50) {
    lastSent = new Map([...lastSent].filter(([, t]) => now - t < DEDUPE_WINDOW_MS));
  }
  lastSent.set(key, now);
  sentTimestamps.push(now);
  return true;
}

function renderContext(options: ReportOptions | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  const source = options?.context ?? {};
  for (const [key, value] of Object.entries(source)) {
    if (value === undefined || value === null) {
      continue;
    }
    out[key.slice(0, 40)] = scrubTelemetryText(String(value)).slice(0, 200);
  }
  if (currentUserId) {
    out.userId = currentUserId;
  }
  if (breadcrumbs.length > 0) {
    // Шаги одной строкой: на сервере это одно значение из `context`, и разбирать его глазами
    // проще, чем дюжину отдельных пар.
    out.шаги = breadcrumbs.map((b) => `${b.scope}:${b.message}`).join(' → ');
  }
  return out;
}

/**
 * Отправить отчёт. Ничего не возвращает и ничего не ждёт от вызывающего: `void reportError(...)`
 * — нормальный способ её позвать.
 */
export async function reportError(
  scope: ErrorScope,
  error: unknown,
  options?: ReportOptions,
): Promise<void> {
  try {
    // Текст ошибки тоже бывает с адресом («HTTP 404 GET /api/…?q=…»): только шаблоном (PRIV-3).
    const message = scrubTelemetryText(messageOf(error));
    if (!allowedToSend(`${scope}|${message}`)) {
      return;
    }
    const token = accessTokenReader ? await accessTokenReader().catch(() => null) : null;
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) {
      // Токен не обязателен — с ним сервер запишет ещё и userId, что удобнее при разборе.
      headers.Authorization = `Bearer ${token}`;
    }
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    try {
      await fetch(`${API_BASE}/api/client-errors`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          level: options?.level ?? 'error',
          scope,
          message: message.slice(0, 500),
          stack: stackOf(error)?.slice(0, 8_000),
          platform: platformName(),
          appVersion: appVersion(),
          osVersion: osVersion(),
          requestId: options?.requestId ?? lastRequestId ?? undefined,
          context: renderContext(options),
        }),
        signal: controller.signal,
      });
    } finally {
      clearTimeout(timer);
    }
  } catch {
    /* см. правило 1 в шапке модуля: отчёт о поломке не имеет права быть поломкой */
  }
}

/** Только для тестов: сбросить накопленное состояние между случаями. */
export function resetErrorReporterForTests(): void {
  breadcrumbs = [];
  currentUserId = null;
  lastRequestId = null;
  lastSent = new Map();
  sentTimestamps = [];
  accessTokenReader = null;
}
